import { type ReactNode, useMemo } from 'react';

import type { FundCarouselItem } from '@/api/fund-views/types';
import { theme, Typography } from '@/design-system';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { FundCarouselCard } from '@/pages/all-funds/modules/fund-carousel-card';
import { FundCarouselCardSkeleton } from '@/pages/all-funds/modules/fund-carousel-card/skeleton';
import { EmptyState } from '@/shared/design-system';

import { Carousel } from './carousel';

const SKELETON_CARD_COUNT = 4;

const MIN_LOOP_SLIDES = 8;

interface FundCarouselProps {
  title: string;
  items: FundCarouselItem[];
  emptyMessage: string;
  visibleCount?: number;
  isLoading?: boolean;
  renderAction?: (_item: FundCarouselItem) => ReactNode;
}

export const FundCarousel = ({
  title,
  items,
  emptyMessage,
  visibleCount = 3,
  isLoading = false,
  renderAction,
}: FundCarouselProps) => {
  const isMobile = useIsMobile();
  const length = items.length;

  const header = (
    <>
      <Typography as="h2" variant="h5" color={theme.colors.text.primary}>
        {title}
      </Typography>

      {isMobile && !isLoading && length > 0 && (
        <Typography variant="body3" color={theme.colors.text.secondary}>
          {`${length} funds`}
        </Typography>
      )}
    </>
  );

  const canSlide = !isMobile && length >= visibleCount;

  const trackItems = useMemo(() => {
    if (!canSlide || length === 0 || length >= MIN_LOOP_SLIDES) return items;

    return Array.from({ length: Math.ceil(MIN_LOOP_SLIDES / length) }, () => items).flat();
  }, [canSlide, items, length]);

  const slides = isLoading
    ? Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
        <FundCarouselCardSkeleton key={index} />
      ))
    : trackItems.map((item, index) => (
        <FundCarouselCard
          key={`${item.fundId}-${index}`}
          data={item}
          action={renderAction?.(item)}
        />
      ));

  return (
    <Carousel
      ariaLabel={title}
      header={header}
      isLoading={isLoading}
      loop={canSlide}
      showNavigation={canSlide}
      emptyState={<EmptyState variant="tertiary" title={emptyMessage} />}
    >
      {slides}
    </Carousel>
  );
};
